<template>
    <ValidationProvider v-slot="{ errors }" name="TPA" rules="required" :value="value && value[0]">
      <AdvancedDropdown
        v-model="modelValue"
        v-bind="$attrs"
        :options="options"
        :disabled="disabled"
        :loading="isFetching && !options.length"
        :can-add-new="false"
        :label="label"
        size="sm"
      >
        <!-- <template v-slot:label> TPA <span class="text-danger">*</span> </template> -->

        <template v-slot:button-content>
          <template v-if="!!selectedOption">{{ selectedOption.text }}</template>
          <template v-else>Select</template>
        </template>

      </AdvancedDropdown>

      <div v-if="!!errors.length" class="invalid-feedback d-block">
        {{ errors[0] }}
      </div>
    </ValidationProvider>
  </template>

  <script>
  import { defineComponent, computed, watch } from '@vue/composition-api'
  import { ValidationProvider } from 'vee-validate'

  import { useRLSGroup } from '@composables'

  import { BaseIcon } from '@components/base'
  import { AdvancedDropdown } from '@components/advanced'

  export default defineComponent({
    name: 'CashCallFormTPA',
    components: {
      ValidationProvider,

      BaseIcon,

      AdvancedDropdown
    },
    props: {
      value: {
        type: Array,
        default: () => []
      },
      disabled: {
        type: Boolean,
        default: false
      },
      // Pass label="" to render the dropdown with no label of its own - used
      // where the surrounding layout already names the field (e.g. the Claim
      // Details summary table, which has its own "TPA" row heading).
      label: {
        type: String,
        default: 'TPA'
      }
    },
    emits: ['input', 'update:selectedTPAId'],
    setup(props, { emit }) {
      const { isFetching, rlsGroupItems } = useRLSGroup('TPA')

      const modelValue = computed({
        get: () => props.value,
        set: (newValue) => emit('input', newValue)
      })

      const options = computed(() =>
        rlsGroupItems.value.map((rlsGroupItem) => ({
          text: rlsGroupItem.TPA,
          value: rlsGroupItem.TPA,
          id: rlsGroupItem.id
        }))
      )

      const selectedOption = computed(() => {
        const name = props.value && props.value[0]
        if (!name) return null
        return options.value.find((opt) => opt.value === name) || null
      })

      // Emit the resolved id from a watcher, NOT from inside the computed
      // above. A computed is re-evaluated whenever anything it reads changes,
      // so emitting there fires on unrelated re-renders, re-enters the parent
      // mid-render and knocks the surrounding ValidationProvider back into an
      // un-validated state - the field then shows "required" despite holding a
      // value. A watcher runs after the update instead, which is where a side
      // effect belongs.
      watch(selectedOption, (option) => {
        emit('update:selectedTPAId', option ? option.id : null)
      }, { immediate: true })

      // The option list, published upward. The manual BDX path (LIQ-1006) can
      // derive a TPA's ID from the key being typed but not its name, and this
      // component is the only place that mapping exists - without it a prefill
      // could set the id while the dropdown still read "Select".
      watch(options, (opts) => {
        emit('update:options', opts)
      }, { immediate: true })

      return {
        isFetching,
        modelValue,
        options,
        selectedOption
      }
    }
  })
  </script>

  <style scoped>
  .advanced-dropdown >>> .dropdown-toggle {
    max-width: 214px;
  }

  .advanced-dropdown >>> .dropdown-menu {
    /* Shorter than the shared 320px: these four sit low in the Claim Details
       summary box, so the menu opens upward and runs off the top of the modal
       (the underwriter list is ~40 entries). ~5 rows plus the search bar. */
    max-height: 210px;
  }

  .advanced-dropdown >>> .dropdown-menu .list-group-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .advanced-dropdown >>> .dropdown-menu .list-group-item.disabled {
    color: #fff;
    background-color: #1c4a50;
    pointer-events: initial;
  }
  </style>
